import { useCallback, useMemo } from 'react';
import { useBatchId } from './useBatchId';
import { NavigateOptions, useNavigate } from 'react-router-dom';
import { useToastMessage } from './useToastMessage';
import { useCallApi } from './useCallApi';
import { useAuth } from './useAuth';
import { getCandidateGroup } from '../service/PreWorkService';

export const usePreworkGroupId = () => {
	const navigate = useNavigate();
	const { navigateToRoute, queryParams, getRouteWithParams } = useBatchId();
	const { showToastMessage } = useToastMessage();
	const { callApi } = useCallApi();
	const { srcmId } = useAuth();

	const preworkGroupId = useMemo(() => {
		const preworkGroupIdParam = Number(queryParams.get('preworkGroupId'));

		if (typeof preworkGroupIdParam !== 'number') {
			return -1;
		}

		return preworkGroupIdParam;
	}, [queryParams]);

	const navigateWithPreworkGroupId = useCallback(
		(route: string, groupIdParam?: number, params?: NavigateOptions) => {
			const groupIdToUse = groupIdParam ? `${groupIdParam}` : preworkGroupId;
			queryParams.set('preworkGroupId', `${groupIdToUse}`);
			navigateToRoute(route, params);
		},
		[preworkGroupId, navigateToRoute, queryParams]
	);

	const navigateWithPreworkBatchIdAndGroupTab = useCallback(
		(route: string, preworkActiveTab?: string) => {
			if (preworkActiveTab) {
				queryParams.set('preworkActiveTab', preworkActiveTab);
			}
			queryParams.delete('preworkGroupId');
			navigate(`${getRouteWithParams(route)}`);
		},
		[getRouteWithParams, navigate, queryParams]
	);

	const fetchGroup = useCallback(async () => {
		if (!preworkGroupId) {
			showToastMessage('error', 'preworkGroupId is missing');
			throw Error('Prework Group Id is missing while fetching group info.');
		}
		if (srcmId) {
			const response = await callApi(
				() =>
					getCandidateGroup({
						functionarySrcmId: srcmId,
						id: preworkGroupId,
					}),
				'errorOccurredWhileLoading'
			);

			return response;
		}
	}, [srcmId, preworkGroupId, callApi, showToastMessage]);

	return {
		preworkGroupId,
		navigateWithPreworkGroupId,
		fetchGroup,
		navigateWithPreworkBatchIdAndGroupTab,
	};
};
